<script setup>
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, maxLength, helpers } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import Spinner from 'next/spinner/Spinner.vue';
import Button from 'next/button/Button.vue';
import Dialog from 'next/dialog/Dialog.vue';
import Input from 'next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';
import Switch from 'next/switch/Switch.vue';
import ComboBox from 'next/combobox/ComboBox.vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  botId: { type: Number, required: true },
});

const store = useStore();
const { t } = useI18n();

const criteria = computed(
  () => store.getters['wgptBotEvalCriteria/getCriteria']
);
const uiFlags = computed(() => store.getters['wgptBotEvalCriteria/getUIFlags']);

const dialogRef = ref(null);
const deleteDialogRef = ref(null);
const isEditing = ref(false);
const editingCriterion = ref(null);

const form = reactive({
  name: '',
  description: '',
  severity: 'warning',
});

const FORM_KEY = 'WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.FORM';

const rules = {
  name: {
    required: helpers.withMessage(
      () => t(`${FORM_KEY}.NAME_REQUIRED`),
      required
    ),
    maxLength: helpers.withMessage(
      () => t(`${FORM_KEY}.NAME_MAX_LENGTH`),
      maxLength(100)
    ),
  },
  description: {
    required: helpers.withMessage(
      () => t(`${FORM_KEY}.DESCRIPTION_REQUIRED`),
      required
    ),
    maxLength: helpers.withMessage(
      () => t(`${FORM_KEY}.DESCRIPTION_MAX_LENGTH`),
      maxLength(500)
    ),
  },
  severity: {
    required: helpers.withMessage(
      () => t(`${FORM_KEY}.SEVERITY_REQUIRED`),
      required
    ),
  },
};

const v$ = useVuelidate(rules, form);

const nameError = computed(() => {
  if (!v$.value.name.$error) return '';
  return v$.value.name.$errors[0]?.$message || '';
});

const descriptionError = computed(() => {
  if (!v$.value.description.$error) return '';
  return v$.value.description.$errors[0]?.$message || '';
});

const severityError = computed(() => {
  if (!v$.value.severity.$error) return '';
  return v$.value.severity.$errors[0]?.$message || '';
});

const severities = [
  { label: 'Info', value: 'info' },
  { label: 'Warning', value: 'warning' },
  { label: 'Critical', value: 'critical' },
];

const severityConfig = {
  info: { color: 'text-n-slate-11', bg: 'bg-n-alpha-2', icon: 'i-lucide-info' },
  warning: {
    color: 'text-n-amber-11',
    bg: 'bg-n-amber-3',
    icon: 'i-lucide-alert-triangle',
  },
  critical: {
    color: 'text-n-ruby-11',
    bg: 'bg-n-ruby-3',
    icon: 'i-lucide-alert-circle',
  },
};

const getSeverityConfig = severity =>
  severityConfig[severity] || severityConfig.info;

const resetForm = () => {
  form.name = '';
  form.description = '';
  form.severity = 'warning';
  v$.value.$reset();
};

const openCreateDialog = () => {
  isEditing.value = false;
  editingCriterion.value = null;
  resetForm();
  nextTick(() => dialogRef.value?.open());
};

const openEditDialog = criterion => {
  isEditing.value = true;
  editingCriterion.value = criterion;
  form.name = criterion.name;
  form.description = criterion.description;
  form.severity = criterion.severity;
  v$.value.$reset();
  nextTick(() => dialogRef.value?.open());
};

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  try {
    const payload = {
      name: form.name,
      description: form.description,
      severity: form.severity,
    };
    if (isEditing.value) {
      await store.dispatch('wgptBotEvalCriteria/update', {
        botId: props.botId,
        id: editingCriterion.value.id,
        ...payload,
      });
      useAlert(t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.API.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('wgptBotEvalCriteria/create', {
        botId: props.botId,
        ...payload,
      });
      useAlert(t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.API.CREATE_SUCCESS'));
    }
    dialogRef.value?.close();
  } catch (error) {
    useAlert(
      error?.message || t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.API.ERROR')
    );
  }
};

const criterionToDelete = ref(null);

const openDeleteDialog = criterion => {
  criterionToDelete.value = criterion;
  nextTick(() => deleteDialogRef.value?.open());
};

const handleDelete = async () => {
  if (!criterionToDelete.value) return;
  try {
    await store.dispatch('wgptBotEvalCriteria/delete', {
      botId: props.botId,
      id: criterionToDelete.value.id,
    });
    useAlert(t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.API.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.message || t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.API.ERROR')
    );
  } finally {
    deleteDialogRef.value?.close();
    criterionToDelete.value = null;
  }
};

const toggleActive = async criterion => {
  try {
    await store.dispatch('wgptBotEvalCriteria/update', {
      botId: props.botId,
      id: criterion.id,
      active: !criterion.active,
    });
  } catch (error) {
    useAlert(
      error?.message || t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.API.ERROR')
    );
  }
};

const seedDefaults = async () => {
  try {
    await store.dispatch('wgptBotEvalCriteria/seed', props.botId);
    useAlert(t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.API.SEED_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.message || t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.API.ERROR')
    );
  }
};

onMounted(() => {
  store.dispatch('wgptBotEvalCriteria/get', props.botId);
});
</script>

<template>
  <div class="flex flex-col gap-4 pb-8">
    <div class="flex items-center justify-between">
      <p class="text-sm text-n-slate-11">
        {{ t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.DESCRIPTION') }}
      </p>
      <div class="flex items-center gap-2">
        <Button
          :label="t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.SEED_DEFAULTS')"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-lucide-wand-2"
          :is-loading="uiFlags.isSeeding"
          @click="seedDefaults"
        />
        <Button
          :label="t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.ADD')"
          size="sm"
          icon="i-lucide-plus"
          @click="openCreateDialog"
        />
      </div>
    </div>

    <div
      v-if="uiFlags.isFetching"
      class="flex items-center justify-center py-16"
    >
      <Spinner />
    </div>

    <template v-else>
      <div
        v-if="!criteria.length"
        class="flex flex-col items-center justify-center py-16 gap-3"
      >
        <Icon icon="i-lucide-list-checks" class="size-8 text-n-slate-9" />
        <p class="text-sm text-n-slate-11">
          {{ t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.EMPTY') }}
        </p>
        <Button
          :label="t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.SEED_DEFAULTS')"
          size="sm"
          icon="i-lucide-wand-2"
          :is-loading="uiFlags.isSeeding"
          @click="seedDefaults"
        />
      </div>

      <div v-else class="flex flex-col gap-3">
        <div
          v-for="criterion in criteria"
          :key="criterion.id"
          class="flex items-start justify-between gap-4 p-4 rounded-xl border border-n-weak transition-opacity"
          :class="{ 'opacity-50': !criterion.active }"
        >
          <div class="flex items-start gap-3 flex-1 min-w-0">
            <div
              class="size-9 flex-shrink-0 rounded-lg flex items-center justify-center"
              :class="getSeverityConfig(criterion.severity).bg"
            >
              <Icon
                :icon="getSeverityConfig(criterion.severity).icon"
                class="size-4"
                :class="getSeverityConfig(criterion.severity).color"
              />
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-0.5">
                <h4 class="text-sm font-medium text-n-slate-12 truncate">
                  {{ criterion.name }}
                </h4>
                <span
                  class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium capitalize"
                  :class="[
                    getSeverityConfig(criterion.severity).color,
                    getSeverityConfig(criterion.severity).bg,
                  ]"
                >
                  {{ criterion.severity }}
                </span>
              </div>
              <p class="text-xs text-n-slate-11 line-clamp-2">
                {{ criterion.description }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-2 flex-shrink-0">
            <Switch
              :model-value="criterion.active"
              @change="toggleActive(criterion)"
            />
            <Button
              v-tooltip.top="
                t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.EDIT_TITLE')
              "
              icon="i-woot-edit-pen"
              sm
              slate
              @click="openEditDialog(criterion)"
            />
            <Button
              v-tooltip.top="t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.DELETE')"
              icon="i-woot-bin"
              sm
              slate
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              @click="openDeleteDialog(criterion)"
            />
          </div>
        </div>
      </div>
    </template>

    <Dialog
      ref="dialogRef"
      type="edit"
      :title="
        isEditing
          ? t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.EDIT_TITLE')
          : t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.CREATE_TITLE')
      "
      :confirm-button-label="
        isEditing
          ? t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.SAVE')
          : t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.CREATE')
      "
      :is-loading="uiFlags.isCreating || uiFlags.isUpdating"
      @confirm="handleSubmit"
    >
      <div class="flex flex-col gap-4">
        <Input
          v-model="form.name"
          :label="t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.FORM.NAME')"
          :placeholder="
            t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.FORM.NAME_PLACEHOLDER')
          "
          :message="nameError"
          :message-type="nameError ? 'error' : 'info'"
          @blur="v$.name.$touch"
        />
        <TextArea
          v-model="form.description"
          custom-text-area-class="!outline !outline-0"
          :label="t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.FORM.DESCRIPTION')"
          :placeholder="
            t(
              'WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.FORM.DESCRIPTION_PLACEHOLDER'
            )
          "
          :max-length="500"
          show-character-count
          :message="descriptionError"
          :message-type="descriptionError ? 'error' : 'info'"
          @blur="v$.description.$touch"
        />
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.FORM.SEVERITY') }}
          </label>
          <ComboBox
            v-model="form.severity"
            :options="severities"
            :placeholder="
              t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.FORM.SEVERITY')
            "
            :has-error="!!severityError"
            :message="severityError"
          />
        </div>
      </div>
    </Dialog>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.DELETE_TITLE')"
      :description="t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.DELETE_CONFIRM')"
      :confirm-button-label="t('WGPT_BOTS.EDIT.TABS.QUALITY.CRITERIA.DELETE')"
      :is-loading="uiFlags.isDeleting"
      @confirm="handleDelete"
    />
  </div>
</template>
